import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Button from "../../../../../../../../../../../components/Button/Button";
import EditIcon from "../../../../../../../../../../../assets/svg/edit-icon";
import TrashIcon from "../../../../../../../../../../../assets/svg/trash-icon";
import { PermissionDisabledWrapper } from "../../../../../../PermissionDisabledWrapper";

interface CommentActionMenuProps {
  triggerRef: React.RefObject<HTMLElement>;
  hideEdit?: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export const CommentActionMenu = ({
  triggerRef,
  hideEdit,
  disabled = false,
  onEdit,
  onDelete,
  onClose,
}: CommentActionMenuProps) => {
  const menuHeight = 100;
  const GAP = 4;

  const portalPosition = (() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return { top: 0, right: 0 };
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const openAbove = spaceBelow < menuHeight;
    return openAbove
      ? {
          bottom: window.innerHeight - rect.top + GAP,
          right: window.innerWidth - rect.right,
        }
      : {
          top: rect.bottom + GAP,
          right: window.innerWidth - rect.right,
        };
  })();

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const scrollParent = triggerRef.current?.closest("[data-activity-scroll]");
    if (!scrollParent) return;
    const originalOverflow = (scrollParent as HTMLElement).style.overflowY;
    (scrollParent as HTMLElement).style.overflowY = "hidden";
    return () => {
      (scrollParent as HTMLElement).style.overflowY = originalOverflow;
    };
  }, [triggerRef]);

  return createPortal(
    <>
      <div
        className="fixed inset-0 overflow-hidden"
        style={{ zIndex: 9999998 }}
        onClick={handleOverlayClick}
      />
      <div
        className="fixed flex flex-col gap-[4px] w-[180px] p-[8px] bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk backdrop-blur-[10px] rounded-[8px] border border-border-gray dark:border-border-gray-dk shadow-popper-blur-effect"
        style={{ ...portalPosition, zIndex: 9999999 }}
      >
        {!hideEdit && (
          <PermissionDisabledWrapper
            disabled={disabled}
            className="w-full"
            tooltipSide="left"
          >
            <Button
              data-note-option-btn
              innerText="Edit"
              Icon={EditIcon}
              iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
              iconLeft={true}
              innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-normal"
              buttonContainerClass="w-full rounded-[4px] justify-start p-[8px] hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
              onClick={() => {
                if (disabled) return;
                onEdit();
              }}
            />
          </PermissionDisabledWrapper>
        )}
        <PermissionDisabledWrapper
          disabled={disabled}
          className="w-full"
          tooltipSide="left"
        >
          <Button
            data-note-option-btn
            innerText="Delete Comment"
            Icon={TrashIcon}
            iconClass="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk"
            iconLeft={true}
            innerTextClass="text-verba-red-alert dark:text-verba-red-alert-dk text-md font-normal"
            buttonContainerClass="w-full rounded-[4px] justify-start p-[8px] hover:bg-tag-red-bg dark:hover:bg-tag-red-bg-dk transition-colors"
            secondaryButtonContainerClass="w-full"
            onClick={() => {
              if (disabled) return;
              onDelete();
            }}
          />
        </PermissionDisabledWrapper>
      </div>
    </>,
    document.body!
  );
};
